<script setup lang="ts">
import { computed } from 'vue';
import {
  Table as ATable,
  Button,
  Tabs,
  TabPane,
} from 'ant-design-vue';
import { array, func } from 'vue-types';

import useFilesColumns from './Search/useFilesColumns';

import Table, { props as properties } from '@/ui/Table/Table.vue';
import type { FileFragmentFragment, FileContentFragmentFragment } from '@/graphql/generated/graphql';
import TableSizeSelect from '@/ui/Table/TableSizeSelect.vue';
import TableColumnSelect from '@/ui/Table/TableColumnSelect.vue';
import useConfig from '@/providers/config/useConfig';
import TableColumn from '@/ui/Table/templates/TableColumn.vue';
import Refetch from '@/ui/Table/templates/Refetch.vue';
import CopyIconButton from '@/ui/CopyIconButton.vue';

const props = defineProps({
  ...properties,
  dataSource: array<FileFragmentFragment>().isRequired,
  getLink: func<(data: FileFragmentFragment) => string>(),
  onClick: func<(data: FileFragmentFragment) => void>(),
  onSelect: func<(data: FileFragmentFragment) => void>(),
  onFileContentSelect: func<(file: FileFragmentFragment, extract: FileContentFragmentFragment) => void>(),
  refetch: func(),
});
const {
  columnsSelected,
  columnsFilter,
  setSorter,
  columns,
} = useFilesColumns({
  orderBy: props.query.orderBy,
});
const config = useConfig();
const env = useEnv();
const tableDensity = computed(() => config.config.tableDensity);
</script>
<template>
  <Table
    :set-sorter="setSorter"
    top-pag-class="flex space-x-4 justify-end mb-4"
    :pagination="pagination"
    :loading="loading"
    :query="query"
    :set-query="setQuery"
    :search="search"
    :set-search="setSearch"
    :hide-search="hideSearch"
    :header-class="headerClass"
    :show-size-changer="false"
  >
    <template #top_pag_right>
      <div class="flex space-x-2">
        <TableColumnSelect v-model="columnsSelected" :columns-filter="columnsFilter" />
        <TableSizeSelect />
        <Refetch v-if="refetch" :refetch="refetch" />
      </div>
    </template>
    <template #default="{ handleTableChange }">
      <ATable
        :data-source="dataSource"
        :columns="columns"
        :row-key="(record) => record.id"
        :pagination="false"
        :size="tableDensity"
        @change="handleTableChange"
      >
        <!-- Column content -->
        <template #bodyCell="{ record, column }">
          <template v-if="column.key === 'download'">
            <Button
              data-test-id="id-download"
              :href="`${env.domains.api}/v2/file/${record.id}?asAttachment=true`"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </Button>
          </template>
          <template v-else-if="column.key === 'uri'">
            <CopyIconButton :value="record.uri" />
          </template>
          <TableColumn
            v-else
            :column="(column.key as string)"
            :record="record"
            :get-link="getLink"
            :on-click="onClick"
            :on-select="onSelect"
          />
        </template>
        <template #expandedRowRender="{ record }">
          <Tabs>
            <TabPane key="ai-generated" tab="AI Generated">
              <FileAiReviewTable :file="record" />
            </TabPane>
            <TabPane key="file-content" tab="File Content">
              <FileContentServiceTable :file="record" />
            </TabPane>
          </Tabs>
        </template>
      </ATable>
    </template>
  </Table>
</template>
